import { yupResolver } from '@hookform/resolvers/yup';
import ContentPasteGoOutlinedIcon from '@mui/icons-material/ContentPasteGoOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import clsx from 'clsx';
import React, { FC, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { PasswordTextfield } from 'src/components/custom/PasswordTextfield';
import { IEditProviderProps } from 'src/components/Providers/editPanel/EditProvider';
import { setNotice } from 'src/redux/noticesSlice';
import * as yup from 'yup';
import { handleCopy, handlePasteToForm, isObjectEmpty } from '../../../../helpers';
import {
  IEsiaParams,
  IProvider,
  useUpdateAvatarMutation,
  useUpdateProviderMutation,
} from '../../../../redux/services/provider';
import { CustomTypography } from '../../../custom/CustomTypography';
import { ConfirmationModal } from '../../../modal/ConfirmationModal';
import { TChips } from '../../../Providers/createPanel/CreateProvider';
import { ProviderHeader } from '../../../Providers/ProviderHeader';
import { SharedFormProvider } from '../../../shared/FormProvider';
import { SidePanel } from '../../../sidePanel/SidePanel';
import styles from './EditProvider.module.css';
import { ENoticeType } from 'src/enums';

const schema = yup.object({
  name: yup
    .string()
    .max(50, 'Название не может превышать 50 символов')
    .required('Обязательное поле')
    .matches(/[^ ]+/, {
      message: 'Название не может состоять только из пробелов',
    })
    .matches(/^[^ ]+( *[^ ]+)*?$/, 'Название не может содержать пробелы в начале и конце'),
  description: yup
    .string()
    .max(255, 'Описание не может превышать 255 символов')
    .matches(/^$|[^ ]+/, {
      message: 'Описание не может состоять только из пробелов',
    }),
  is_public: yup.boolean(),
  params: yup.object({
    password: yup.string().max(255, 'Пароль не может превышать 255 символов'),
    external_client_id: yup
      .string()
      .max(255, 'Секретный ключ не может превышать 255 символов')
      .required('Обязательное поле')
      .matches(/^[^\n ]*$/, {
        message: 'Секретный ключ не может содержать пробелы',
      }),
    authorization_endpoint: yup
      .string()
      .url('Неверный формат ссылки')
      .max(2000, 'Адрес не может превышать 2000 символов')
      .required('Обязательное поле'),
    token_endpoint: yup
      .string()
      .url('Неверный формат ссылки')
      .max(2000, 'Адрес не может превышать 2000 символов')
      .required('Обязательное поле'),
    issuer: yup
      .string()
      .url('Неверный формат ссылки')
      .max(2000, 'Адрес не может превышать 2000 символов')
      .required('Обязательное поле'),
    userinfo_endpoint: yup
      .string()
      .url('Неверный формат ссылки')
      .max(2000, 'Адрес не может превышать 2000 символов')
      .required('Обязательное поле'),
    sign_endpoint: yup
      .string()
      .url('Неверный формат ссылки')
      .max(2000, 'Адрес не может превышать 2000 символов')
      .required('Обязательное поле'),
    verify_endpoint: yup
      .string()
      .url('Неверный формат ссылки')
      .max(2000, 'Адрес не может превышать 2000 символов')
      .required('Обязательное поле'),
    certificate: yup.string().required('Обязательное поле'),
  }),
});

export const EditEsiaProvider: FC<IEditProviderProps> = ({
  isOpen,
  onClose,
  provider,
  defaultAvatar,
}) => {
  const dispatch = useDispatch();
  const methods = useForm<IProvider<IEsiaParams>>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, dirtyFields },
    clearErrors,
    reset,
    control,
  } = methods;

  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [chips, setChips] = useState<TChips[]>([]);

  const [updateProvider, updateResult] = useUpdateProviderMutation();
  const [updateAvatar] = useUpdateAvatarMutation();

  const uploadedAvatar = useWatch({ control, name: 'avatar' });

  useEffect(() => {
    if (updateResult.isSuccess) onClose();
  }, [updateResult]);

  useEffect(() => {
    if (provider) {
      reset(provider as IProvider<IEsiaParams>);
      setChips([]);
    }
  }, [isOpen]);

  const closeSaveModal = () => setSaveModalOpen(false);

  const handleClose = () => {
    if (isObjectEmpty(dirtyFields)) onClose();
    else setSaveModalOpen(true);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as ArrayBuffer;
        const base64Content = arrayBufferToBase64(content);
        setValue('params.certificate', base64Content, { shouldDirty: true });
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  };

  const handleButtonClick = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.crt,.pem,.cer'; // Укажите нужные форматы файлов
    fileInput.addEventListener('change', handleFileChange as unknown as EventListener);
    fileInput.click();
  };

  const pasteFields = async () => {
    try {
      await handlePasteToForm(methods);
    } catch (err) {
      dispatch(
        setNotice({
          type: ENoticeType.error,
          id: Math.random(),
          isRead: false,
          message: `Ошибка вставки параметров: ${err}`,
          timestamp: new Date().toString(),
        }),
      );
    }
  };

  const onSubmit: SubmitHandler<IProvider<IEsiaParams>> = (data) => {
    const scopes = chips.map((chip) => chip.value).join(' ');
    updateProvider({
      ...data,
      params: {
        ...data.params,
        scopes,
      },
    } as IProvider<IEsiaParams>).then(() => {
      setTimeout(() => {
        updateAvatar({
          clientId: data.client_id,
          providerId: data.id,
          avatar: uploadedAvatar,
        });
      }, 1000);
    });
  };

  return (
    <SidePanel
      onClose={handleClose}
      isOpen={isOpen}
      title="Редактировать способ входа ЕСИА"
      AdditionalAction={() => pasteFields()}
      customAdditionalText="Вставить настройки"
      CustomAdditionalIcon={ContentPasteGoOutlinedIcon}
      isNoBackdrop
    >
      <SharedFormProvider<IProvider<IEsiaParams>>
        methods={methods}
        onSubmit={handleSubmit(onSubmit)}
        handleCancel={handleClose}
        isDisabled={updateResult.isLoading || isObjectEmpty(dirtyFields)}
      >
        <ProviderHeader defaultAvatar={defaultAvatar} />
        <CustomTypography className={clsx('text-14', styles.asterisk, styles['input-title'])}>
          Идентификатор ресурса (client_id)
        </CustomTypography>
        <TextField
          {...register('params.external_client_id', {
            required: true,
            onChange: () => {
              if (errors.params?.external_client_id) clearErrors('params.external_client_id');
            },
          })}
          className="custom"
          error={!!errors.params?.external_client_id}
          helperText={errors.params?.external_client_id?.message}
          fullWidth
          variant="standard"
          autoComplete="off"
        />
        <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
          Уникальный идентификатор подключаемого ресурса
        </CustomTypography>
        <CustomTypography className={clsx('text-14', styles['input-title'])}>
          Пароль
        </CustomTypography>
        <PasswordTextfield
          nameField="params.password"
        />
        <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
          Пароль от ключевого контейнера
        </CustomTypography>
        <CustomTypography className={clsx('text-14', styles.asterisk, styles['input-title'])}>
          Базовый адрес сервиса (issuer)
        </CustomTypography>
        <TextField
          {...register('params.issuer', {
            required: true,
            onChange: () => {
              if (errors.params?.issuer) clearErrors('params.issuer');
            },
          })}
          className="custom"
          error={!!errors.params?.issuer}
          helperText={errors.params?.issuer?.message}
          fullWidth
          variant="standard"
          autoComplete="off"
        />
        <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
          Тестовый базовый адрес сервиса https://esia-portal1.test.gosuslugi.ru
        </CustomTypography>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <CustomTypography className={clsx('text-14', styles.asterisk, styles['input-title'])}>
            Сертификат подписи и проверки запросов
          </CustomTypography>
          <Button variant="custom2" className={styles['input-title']} onClick={handleButtonClick}>
            Загрузить
          </Button>
        </div>
        <Controller
          name="certificate"
          render={({ field }) => (
            <>
              <TextField
                {...field}
                className={styles.certInput}
                error={!!errors.params?.certificate}
                helperText={errors.params?.certificate?.message}
              />
              {field.value && (
                <div className={styles.certContainer}>
                  <DescriptionOutlinedIcon className={styles.certIcon} />
                  <Button
                    variant="custom"
                    component="label"
                    color="secondary"
                    onClick={() => setValue('params.certificate', '')}
                  >
                    удалить
                  </Button>
                  <Button
                    variant="custom"
                    component="label"
                    color="secondary"
                    onClick={() => {
                      const certificateValue = watch('params.certificate');
                      handleCopy(certificateValue);
                    }}
                  >
                    копировать
                  </Button>
                </div>
              )}
              {!!errors.params?.certificate && (
                <CustomTypography className="text-14" color="error">
                  {errors.params?.certificate?.message}
                </CustomTypography>
              )}
            </>
          )}
        />
        <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
          Сертификат, который с помощью которого подписываются и проверяются запросы
        </CustomTypography>
        <CustomTypography className={clsx('text-14', styles.asterisk, styles['input-title'])}>
          Адрес получения подписи запроса (sign_endpoint)
        </CustomTypography>
        <TextField
          {...register('params.sign_endpoint', {
            required: true,
            onChange: () => {
              if (errors.params?.sign_endpoint) clearErrors('params.sign_endpoint');
            },
          })}
          className="custom"
          error={!!errors.params?.sign_endpoint}
          helperText={errors.params?.sign_endpoint?.message}
          fullWidth
          variant="standard"
        />
        <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
          Ресурс, который подписывает запрос
        </CustomTypography>
        <CustomTypography className={clsx('text-14', styles.asterisk, styles['input-title'])}>
          Адрес проверки подписи запроса (verify_endpoint)
        </CustomTypography>
        <TextField
          {...register('params.verify_endpoint', {
            required: true,
            onChange: () => {
              if (errors.params?.verify_endpoint) clearErrors('params.verify_endpoint');
            },
          })}
          className="custom"
          error={!!errors.params?.verify_endpoint}
          helperText={errors.params?.verify_endpoint?.message}
          fullWidth
          variant="standard"
        />
        <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
          Ресурс, который проверяет подпись ответа от ЕСИА
        </CustomTypography>
      </SharedFormProvider>

      <ConfirmationModal
        title="Сохранение изменений"
        mainMessage={['Изменения не сохранены. Продолжить без сохранения?']}
        actionButtonText="Продолжить"
        isOpen={saveModalOpen}
        onAction={() => {
          onClose();
          setSaveModalOpen(false);
        }}
        onClose={closeSaveModal}
      />
    </SidePanel>
  );
};
